import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import Container from './common/Container';
import Logo from './common/Logo';

export default function Footer() {
    const currentYear = new Date().getFullYear();

    const containerVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
    };

    return (
        <footer className="bg-background text-primary pt-24 pb-8 relative overflow-hidden">
            {/* Background Gradients */}
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-secondary/5 rounded-full blur-[120px] pointer-events-none -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary/5 rounded-full blur-[100px] pointer-events-none translate-y-1/2 -translate-x-1/2" />

            <Container className="relative z-10 font-sans">

                {/* CTA Section */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={containerVariants}
                    className="flex flex-col md:flex-row justify-between items-start md:items-end border-b border-primary/10 pb-16 mb-12 md:mb-20 gap-8"
                >
                    <div className="max-w-2xl">
                        <motion.span variants={itemVariants} className="block text-secondary text-sm tracking-[0.2em] mb-4 uppercase font-bold">
                            Start Your Journey
                        </motion.span>
                        <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl lg:text-5xl  font-serif text-primary font-bold leading-tight">
                            Ready to build your <br />
                            <span className="block mt-2 leading-tight">dream legacy?</span>
                        </motion.h2>
                        {/* <h2 className="text-3xl md:text-4xl lg:text-5xl  font-serif text-primary font-bold leading-tight">
                            <RevealText text="Crafting Landmarks," className='leading-tight' />
                            <span className=" block mt-2 leading-tight">Defining Luxury.</span>
                        </h2> */}
                    </div>

                    <motion.div variants={itemVariants} className="w-full md:w-auto">
                        <Link
                            to="/contact"
                            className="group flex items-center justify-center w-full md:w-auto px-8 py-4 bg-primary text-white rounded-full hover:bg-secondary hover:text-white transition-all duration-300 gap-3 shadow-lg"
                        >
                            <span className="text-sm font-bold tracking-widest uppercase">Get in Touch</span>
                            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </motion.div>
                </motion.div>

                {/* Main Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-12 gap-8 md:gap-12 lg:gap-0 mb-16 md:mb-24">

                    {/* Brand Column */}
                    <div className="col-span-2 md:col-span-1 lg:col-span-3 flex flex-col items-start order-1">
                        <Link to="/" className="mb-6 md:mb-8 flex items-center gap-3">
                            <Logo />
                            <div className="flex flex-col items-start leading-none">
                                <span className="text-2xl md:text-3xl font-serif font-bold tracking-[0.15em] text-primary">VALLETTA</span>
                                <span className="text-[10px] tracking-[0.4em] text-secondary font-bold mt-1">GROUP</span>
                            </div>
                        </Link>
                        <p className="text-secondary text-[15px] font-medium leading-relaxed mb-8 max-w-sm">
                            Crafting landmarks that redefine luxury living. Dedicated to excellence, innovation, and trust in every brick we lay.
                        </p>

                        <div className="flex gap-4">
                            {/* <a
                                href="https://facebook.com"
                                target="_blank"
                                rel="noreferrer"
                                className="w-10 h-10 rounded-full border border-primary/10 flex items-center justify-center text-primary hover:bg-secondary hover:border-secondary hover:text-white transition-all duration-300"
                            >
                                <Facebook size={16} />
                            </a> */}
                            <a
                                href="https://www.instagram.com/valletta_group?igsh=MXdxMW4wd3Fhamhodg=="
                                target="_blank"
                                rel="noreferrer"
                                className="w-10 h-10 rounded-full border border-primary/50 flex items-center justify-center text-primary hover:bg-secondary hover:border-secondary hover:text-white transition-all duration-300"
                            >
                                <Instagram size={16} />
                            </a>
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div className="col-span-1 lg:col-span-3 order-4 lg:order-2">
                        <h4 className="text-primary font-serif font-medium text-lg mb-4 md:mb-6 lg:text-center">Company</h4>
                        <ul className="space-y-2 md:space-y-3">
                            {['About', 'Gallery', 'Careers', 'Contact'].map((item) => (
                                <li key={item}>
                                    <Link
                                        to={`/${item.toLowerCase().replace(' ', '-')}`}
                                        className="text-secondary text-base transition-colors flex items-center lg:justify-center gap-1 group"
                                    >
                                        <span className="w-0 group-hover:w-2 h-[1px] bg-secondary transition-all duration-300"></span>
                                        {item}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Projects Links */}
                    <div className="col-span-1 lg:col-span-3 order-3">
                        <h4 className="text-primary font-serif font-medium text-lg mb-4 md:mb-6 lg:text-center">Projects</h4>
                        <ul className="space-y-2 md:space-y-3">
                            {['Projects', 'Location'].map((item) => (
                                <li key={item}>
                                    <Link
                                        to={`/${item === 'Projects' ? 'projects' : item === 'Location' ? 'location' : ''}`}
                                        className="text-secondary text-base transition-colors flex items-center lg:justify-center gap-1 group"
                                    >
                                        <span className="w-0 group-hover:w-2 h-[1px] bg-secondary transition-all duration-300"></span>
                                        {item}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact Info */}
                    <div className="order-2 lg:order-4 col-span-2 md:col-span-1 lg:col-span-3 bg-white p-6 border border-primary/5 shadow-lg rounded-2xl">
                        <h4 className="text-primary font-serif text-lg mb-4 md:mb-6 font-medium">Head Office</h4>
                        <div className="space-y-4 md:space-y-6">
                            <div className="flex items-start gap-4">
                                <MapPin className="text-secondary shrink-0 mt-1" size={18} />
                                <a href='https://maps.app.goo.gl/5VGqxK8hZNE1YWAw9' target='_blank' rel="noopener noreferrer" className="text-sm text-secondary leading-relaxed hover:text-primary transition-colors">
                                    VALLETTA VISTA, Palanpur, <br />
                                    Nr. Jalaram School, <br />
                                    Surat - 395009.
                                </a>
                            </div>
                            <div className="flex items-start gap-4">
                                <Phone className="text-secondary shrink-0 mt-1" size={18} />
                                <div className="flex flex-col gap-1">
                                    <a href="tel:+919979358000" className="text-sm text-secondary/90 hover:text-primary transition-colors whitespace-nowrap">+91 99793 58000</a>
                                    <a href="tel:+919979259000" className="text-sm text-secondary/90 hover:text-primary transition-colors whitespace-nowrap">+91 99792 59000</a>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <Mail className="text-secondary shrink-0" size={18} />
                                <a href="mailto:vallettagroup12@gmail.com" target='_blank' className="text-sm text-secondary/90 hover:text-primary transition-colors">
                                    vallettagroup12@gmail.com
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="border-t border-primary/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-light">
                    <p>&copy; {currentYear} VALLETTA GROUP. ALL RIGHTS RESERVED.</p>
                </div>
            </Container>
            {/* Massive Watermark - Absolute Bottom */}
            {/* <div className="hidden md:block absolute bottom-[30px] lg:bottom-[8%] left-1/2 -translate-x-1/2 w-full text-center pointer-events-none select-none opacity-[0.03]">
                <h1 className="text-[15vw] font-serif font-bold text-primary leading-none tracking-tight">VALLETTA</h1>
            </div> */}
            <div className="hidden md:flex absolute top-[50px] lg:top-[50%] left-1/2 -translate-x-1/2 w-[10vw] justify-center pointer-events-none select-none opacity-[0.05]">
                <svg xmlns="http://www.w3.org/2000/svg" id="Layer_1" data-name="Layer 1" viewBox="0 0 591.94 793.7" className="w-[30vw] h-auto">
                    <path fill="currentColor" d="M403.23,31.35v6.91q-.21,301.47-.41,602.94c0,14.67.28,29.35.5,44,0,2.62-.41,4.66-2.63,6.56-15.33,13.17-30.48,26.54-45.76,39.76-11.09,9.6-22.29,19.06-33.47,28.56-1,.88-2.27,1.53-4.21,2.82-.1-2.51-.24-4.21-.23-5.91q0-26.84.06-53.68.24-169.4.46-338.79c0-70.34-.22-140.67-.32-211,0-5.37.19-10.74.27-16.11.22-13.75,4.55-26.13,13.45-36.54,8.12-9.51,16.8-18.56,25.42-27.63,13.2-13.87,26.66-27.49,41.72-39.41,1.34-1.07,2.7-2.12,4.06-3.17Z" />
                    <path fill="currentColor" d="M191.82,30.42C198.37,36.71,205,43,211.46,49.29q18.25,17.77,36.43,35.65Q253,90,257.76,95.53c10.11,11.79,16.05,25.29,16.46,41,.16,6.08.44,12.16.44,18.24q-.11,236.43-.22,472.88c0,42.4.58,84.8.87,127.2,0,2.43-.13,4.85-.23,8.44-2.33-1.81-3.75-2.79-5-3.92-15.57-13.7-31.05-27.51-46.71-41.12q-15.17-13.2-30.78-25.9c-2.52-2.06-3.29-4.23-3.27-7.35.46-84.8,1.05-169.61,1.19-254.41q.3-190.83.06-381.64V30.79Z" />
                    <path fill="currentColor" d="M56.55,139c2.71,2.27,5.53,4.41,8.1,6.83Q89,168.86,113.32,192c7.16,6.81,13.59,14.18,18.52,22.84a61.69,61.69,0,0,1,8.57,29.56c.1,4.11.21,8.22.23,12.34q.72,139,1.36,278c.1,31.84-.53,63.69-.85,95.53,0,1.75-.21,3.5-.39,6.41A62.22,62.22,0,0,1,135.6,633c-15.68-13.85-31.44-27.61-46.93-41.67-10.19-9.24-20.33-18.56-29.06-29.3a20.08,20.08,0,0,1-4.83-13.65q.44-61.18.5-122.37,0-137.42-.3-274.82V139.53Z" />
                    <path fill="currentColor" d="M536.86,139v9.59q-.24,161.83-.44,323.65c0,25.94.37,51.88.73,77.82.07,4.68-.58,8.93-3.73,12.49-4.13,4.68-8,9.63-12.64,13.81C507,588.82,492.93,601,479,613.31c-6,5.34-11.87,10.87-17.91,16.19-2.77,2.45-5.8,4.6-8.81,7-2.11-2.3-1.45-4.55-1.45-6.55,0-25.94.11-51.89.12-77.83q0-131.22.07-262.46c0-15.56.39-31.12.57-46.68.1-9.2,2.94-17.49,7.66-25.35,6.89-11.47,16-21,25.44-30.32,10.85-10.63,21.6-21.38,32.45-32,4.59-4.5,9.28-8.91,14-13.3,1.4-1.3,3-2.35,4.53-3.52Z" />
                </svg>
            </div>
        </footer>
    );
}
